'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Phone, ChevronRight } from 'lucide-react';
import siteTheme from '@/lib/theme';
import type { SiteSettings, CmsHeader, CmsActionItem } from '@/lib/cms';
const { colors, fonts } = siteTheme;
const navy = '#0D2F44';



/* ── Logo ── */
function Logo({
  logo, logoSecondary, siteName, scrolled,
}: {
  logo?: { url: string; alternativeText?: string | null } | null;
  logoSecondary?: { url: string; alternativeText?: string | null } | null;
  siteName: string;
  scrolled: boolean;
}) {
  const showSecondary = scrolled && !!logoSecondary;
  return (
    <Link href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
      {(logo || logoSecondary) ? (
        <div style={{
          position: 'relative',
          height: scrolled ? '3.6rem' : '7.5rem',
          width: scrolled ? '9rem' : '15rem',
          transition: 'height 0.5s ease, width 0.5s ease',
        }}>
          {/* Primary logo — fades out on scroll when secondary exists */}
          {logo && (
            <div style={{
              position: 'absolute', inset: 0,
              transition: 'opacity 0.5s ease',
              opacity: showSecondary ? 0 : 1,
            }}>
              <Image src={logo.url} alt={logo.alternativeText || siteName} fill priority sizes="(max-width: 768px) 144px, 240px" style={{ objectFit: 'contain', objectPosition: 'left' }} />
            </div>
          )}
          {/* Secondary logo — fades in on scroll */}
          {logoSecondary && (
            <div style={{
              position: 'absolute', inset: 0,
              transition: 'opacity 0.5s ease',
              opacity: showSecondary ? 1 : 0,
            }}>
              <Image src={logoSecondary.url} alt={logoSecondary.alternativeText || siteName} fill priority sizes="(max-width: 768px) 144px, 240px" style={{ objectFit: 'contain', objectPosition: 'left' }} />
            </div>
          )}
        </div>
      ) : (
        <span style={{
          fontFamily: fonts.script,
          fontSize: scrolled ? '2.4rem' : '3rem',
          fontWeight: 400,
          color: '#fff',
          lineHeight: 1,
          transition: 'font-size 0.5s ease',
        }}>
          {siteName}
        </span>
      )}
    </Link>
  );
}

/* ── Hamburger (same as NavbarV1) ── */
function Hamburger({ open, color = '#fff' }: { open: boolean; color?: string }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      alignItems: 'flex-end', gap: '5px',
      width: '2.8rem', height: '2.8rem', cursor: 'pointer',
    }}>
      <span style={{
        display: 'block', width: '2.2rem', height: '1px', background: color,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        transform: open ? 'translateY(6px) rotate(45deg)' : 'none',
      }} />
      <span style={{
        display: 'block', width: '1.4rem', height: '1px', background: color,
        transition: 'opacity 0.3s ease, width 0.3s ease',
        opacity: open ? 0 : 1,
      }} />
      <span style={{
        display: 'block', width: '2.2rem', height: '1px', background: color,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        transform: open ? 'translateY(-6px) rotate(-45deg)' : 'none',
      }} />
    </div>
  );
}

/* ── Desktop action item (same divider pattern as NavbarV1 hero theme) ── */
function ActionItem({ action }: { action: CmsActionItem }) {
  const divider: React.CSSProperties = {
    paddingRight: '2.4rem',
    borderRight: '1px solid rgba(255,255,255,0.25)',
    marginRight: '2.4rem',
    whiteSpace: 'nowrap',
  };

  switch (action.type) {
    case 'phone':
      return (
        <a
          href={`tel:${(action.phone_number || '').replace(/\s/g, '')}`}
          className="hidden xl:flex"
          style={{
            fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
            letterSpacing: '0.05em',
            color: action.text_color || 'rgba(255,255,255,0.8)',
            textDecoration: 'none',
            alignItems: 'center', gap: '0.6rem',
            ...divider,
          }}
        >
          <Phone size={16} strokeWidth={1.8} color={colors.green} />
          <span>{action.phone_number}</span>
        </a>
      );

    case 'text_link':
      return (
        <Link
          href={action.url || '#'}
          target={action.open_in_new_tab ? '_blank' : undefined}
          className="hidden lg:block"
          style={{
            fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: action.text_color || 'rgba(255,255,255,0.8)',
            textDecoration: 'none',
            ...divider,
          }}
        >
          {action.text}
        </Link>
      );

    case 'button':
      return (
        <Link
          href={action.url || '#'}
          target={action.open_in_new_tab ? '_blank' : undefined}
          className="hidden lg:inline-flex btn-luxury-nav"
          style={{
            fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: action.text_color || '#fff',
            background: action.background || colors.green,
            border: '1px solid rgba(255,255,255,0.2)',
            padding: '1rem 2.6rem', borderRadius: '0.5rem',
            textDecoration: 'none', whiteSpace: 'nowrap',
            alignItems: 'center',
          }}
        >
          {action.text}
        </Link>
      );

    case 'label':
      return (
        <span
          className="hidden lg:block"
          style={{
            fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: action.text_color || 'rgba(255,255,255,0.8)',
            ...divider,
          }}
        >
          {action.text}
        </span>
      );

    default:
      return null;
  }
}

/* ── Main NavbarV2 ── */
export default function NavbarV2({ settings, header }: { settings?: SiteSettings | null; header?: CmsHeader | null }) {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const siteName      = header?.navbar_center?.brand_name || settings?.site_name || 'Sandy Resort';
  const logo          = header?.logos?.logo_full ?? header?.logos?.logo_compact ?? null;
  const logoSecondary = header?.logos?.logo_secondary ?? null;
  const actions  = header?.right_actions ?? [];
  const navLinks = header?.nav_links?.filter(l => l.menu_position === 'center') ?? [];

  /* Mobile dropdown needs extracted values */
  const phoneAction  = actions.find(a => a.type === 'phone') ?? null;
  const phone        = phoneAction?.phone_number ?? null;
  const buttonActions   = actions.filter(a => a.type === 'button');
  const textLinkActions = actions.filter(a => a.type === 'text_link');
  const mobileGridCols  = buttonActions.length + textLinkActions.length;

  const bgStyle: React.CSSProperties = scrolled
    ? { background: `${navy}d9`, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderColor: 'rgba(255,255,255,0.1)', boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }
    : { background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderColor: 'transparent' };

  return (
    <>
      {/* ── Mobile Top Header ── */}
      <div className="flex lg:hidden fixed top-0 left-0 w-full z-[60] px-4 py-2 justify-between items-center text-xs font-sans font-medium" style={{ backgroundColor: colors.gold, color: colors.navyDeep }}>
        {settings?.whatsapp_number && (
          <a href={`tel:${settings.whatsapp_number.replace(/\s/g, '')}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none', color: 'inherit' }}>
            <Phone size={12} /> <span>Mob: {settings.whatsapp_number}</span>
          </a>
        )}
        {settings?.phone && (
          <a href={`tel:${settings.phone.replace(/\s/g, '')}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none', color: 'inherit' }}>
            <Phone size={12} /> <span>Tel: {settings.phone}</span>
          </a>
        )}
      </div>

      {/* ── Fixed header ── */}
      <header className="top-[32px] lg:top-0" style={{
        position: 'fixed', left: 0, right: 0, zIndex: 50,
        borderBottom: '1px solid transparent',
        transition: 'background 0.5s ease, box-shadow 0.5s ease, border-color 0.5s ease',
        ...bgStyle,
      }}>

        {/* ── Main bar ── */}
        <div style={{
          maxWidth: '1600px', margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: scrolled ? `0.9rem clamp(2rem,4vw,5rem)` : `1.2rem clamp(2rem,4vw,5rem)`,
          transition: 'padding 0.5s ease',
          gap: '2rem',
        }}>

          {/* Logo */}
          <div style={{ flexShrink: 0, minWidth: 0 }}>
            <Logo logo={logo} logoSecondary={logoSecondary} siteName={siteName} scrolled={scrolled} />
          </div>

          {/* Center nav — desktop xl+ */}
          <nav className="hidden xl:block" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(1.4rem,2vw,2.8rem)' }}>
              {navLinks.map(link => {
                const active = pathname === link.href || (link.href !== '/' && pathname.startsWith(link.href));
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    target={link.open_in_new_tab ? '_blank' : undefined}
                    style={{
                      position: 'relative',
                      fontFamily: fonts.sans,
                      fontSize: '1.4rem',
                      fontWeight: active ? 500 : 400,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: active ? '#fff' : 'rgba(255,255,255,0.75)',
                      textDecoration: 'none',
                      paddingBottom: '0.4rem',
                      transition: 'color 0.3s ease',
                    }}
                    onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                    onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.75)'; }}
                  >
                    {link.label}
                    {active && (
                      <span style={{
                        position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
                        height: '3px', width: '70%', borderRadius: '9999px',
                        background: `linear-gradient(90deg, #028a91, #0d8691, #1b5c8d)`,
                        boxShadow: '0 0 12px rgba(212,169,107,0.75)',
                      }} />
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Right actions — fully dynamic, same divider pattern as NavbarV1 */}
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: 'auto' }}>
            {actions.map(a => <ActionItem key={a.id} action={a} />)}

            {/* Mobile hamburger */}
            <div className="hamburger-wrap block lg:hidden" onClick={() => setMenuOpen(v => !v)} style={{ cursor: 'pointer' }}>
              <Hamburger open={menuOpen} color="#fff" />
            </div>
          </div>
        </div>

        {/* ── Mobile dropdown ── */}
        <div
          className="lg:hidden"
          style={{
            overflow: 'hidden',
            maxHeight: menuOpen ? '50rem' : '0',
            opacity: menuOpen ? 1 : 0,
            transition: 'max-height 0.5s ease, opacity 0.4s ease',
          }}
        >
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            background: `${navy}f5`,
            backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
            padding: '2rem 2rem 2.4rem',
          }}>

            {/* Nav links */}
            <nav style={{ display: 'flex', flexDirection: 'column' }}>
              {navLinks.map(link => {
                const active = pathname === link.href || (link.href !== '/' && pathname.startsWith(link.href));
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    target={link.open_in_new_tab ? '_blank' : undefined}
                    onClick={() => setMenuOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '1.2rem 0.8rem',
                      borderRadius: '0.5rem',
                      fontFamily: fonts.sans,
                      fontSize: '1.4rem',
                      fontWeight: active ? 500 : 400,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: active ? '#fff' : 'rgba(255,255,255,0.75)',
                      background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                      textDecoration: 'none',
                      transition: 'background 0.2s ease, color 0.2s ease',
                    }}
                  >
                    <span>{link.label}</span>
                    <ChevronRight size={14} strokeWidth={1.5} style={{ opacity: 0.5 }} />
                  </Link>
                );
              })}
            </nav>

            {/* Mobile action buttons — dynamic grid from right_actions */}
            {mobileGridCols > 0 && (
              <div style={{
                marginTop: '1.6rem', paddingTop: '1.6rem',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                display: 'grid',
                gridTemplateColumns: `repeat(${mobileGridCols}, 1fr)`,
                gap: '0.8rem',
              }}>
                {textLinkActions.map(a => (
                  <Link
                    key={a.id}
                    href={a.url || '#'}
                    target={a.open_in_new_tab ? '_blank' : undefined}
                    onClick={() => setMenuOpen(false)}
                    style={{
                      fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
                      letterSpacing: '0.12em', textTransform: 'uppercase',
                      color: a.text_color || 'rgba(255,255,255,0.8)',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      padding: '1rem', borderRadius: '0.5rem',
                      textDecoration: 'none', textAlign: 'center',
                    }}
                  >
                    {a.text}
                  </Link>
                ))}
                {buttonActions.map(a => (
                  <Link
                    key={a.id}
                    href={a.url || '#'}
                    target={a.open_in_new_tab ? '_blank' : undefined}
                    onClick={() => setMenuOpen(false)}
                    className="btn-luxury-nav"
                    style={{
                      fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
                      letterSpacing: '0.18em', textTransform: 'uppercase',
                      color: a.text_color || '#fff',
                      background: a.background || colors.green,
                      padding: '1rem', borderRadius: '0.5rem',
                      textDecoration: 'none', textAlign: 'center',
                      display: 'block',
                    }}
                  >
                    {a.text}
                  </Link>
                ))}
              </div>
            )}

            {/* Phone at bottom */}
            {phone && (
              <a
                href={`tel:${phone.replace(/\s/g, '')}`}
                onClick={() => setMenuOpen(false)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.9rem',
                  marginTop: '1.4rem',
                  fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
                  letterSpacing: '0.06em',
                  color: phoneAction?.text_color || 'rgba(255,255,255,0.75)',
                  textDecoration: 'none',
                  justifyContent: 'center',
                }}
              >
                <Phone size={16} strokeWidth={1.8} color={colors.green} />
                <span>{phone}</span>
              </a>
            )}
          </div>
        </div>

        {/* Gradient rule */}
        <div style={{
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
          opacity: scrolled ? 1 : 0.7,
          transition: 'opacity 0.5s ease',
          maxWidth: '1600px', margin: '0 auto',
        }} />
      </header>

      {/* No spacer — V2 sits over hero content */}
    </>
  );
}
